/**
 * <script-configurator> — shows the mod list that matches the selected game,
 * keeps the live summary and the combined "mods" line-item property in sync,
 * and plays a short "building your script" terminal animation.
 */
class ScriptConfigurator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.profile = null;
    this.onInput = () => this.update();
    this.form.addEventListener('input', this.onInput);
    this.form.addEventListener('change', this.onInput);
    this.update();
  }

  disconnectedCallback() {
    clearTimeout(this.buildTimer);
    clearTimeout(this.debounce);
    if (!this.form) return;
    this.form.removeEventListener('input', this.onInput);
    this.form.removeEventListener('change', this.onInput);
  }

  applyProfile(profile) {
    if (profile === this.profile) return;
    const first = this.profile === null;
    this.profile = profile;
    for (const group of this.querySelectorAll('[data-mod-group]')) {
      const active = group.dataset.modGroup === profile;
      group.hidden = !active;
      if (active && !first) {
        group.querySelectorAll('[data-mod]').forEach((box, i) => { box.checked = i < 2; });
      }
    }
  }

  selectedMods() {
    const group = this.querySelector(`[data-mod-group="${this.profile}"]`);
    return group ? [...group.querySelectorAll('[data-mod]:checked')].map((el) => el.value) : [];
  }

  build() {
    const box = this.querySelector('[data-build]');
    const log = this.querySelector('[data-build-log]');
    const bar = this.querySelector('[data-build-bar]');
    if (!box || !log) return;
    const d = box.dataset;
    const platform = this.form.querySelector('[data-summary="platform"]:checked');
    const style = this.form.querySelector('[data-summary="style"]:checked');
    const lines = [d.lInit, `${d.lTarget} ${this.gameLabel()} · ${platform ? platform.value : ''}`];
    this.selectedMods().forEach((m) => lines.push(`${d.lMod} ${m}`));
    if (style) lines.push(`${d.lTune} ${style.value}`);
    lines.push(d.lCompile, d.lDone);

    const status = this.querySelector('[data-build-status]');
    clearTimeout(this.buildTimer);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      log.textContent = lines.join('\n');
      if (bar) bar.style.width = '100%';
      if (status) status.textContent = status.dataset.ready;
      return;
    }
    if (status) status.textContent = status.dataset.building;
    log.textContent = '';
    log.classList.add('is-typing');
    let i = 0;
    const step = () => {
      log.textContent += (i ? '\n' : '') + lines[i];
      log.scrollTop = log.scrollHeight;
      i += 1;
      if (bar) bar.style.width = `${Math.round((i / lines.length) * 100)}%`;
      if (i < lines.length) {
        this.buildTimer = setTimeout(step, i === lines.length - 1 ? 700 : 220);
      } else {
        log.classList.remove('is-typing');
        if (status) status.textContent = status.dataset.ready;
      }
    };
    this.buildTimer = setTimeout(step, 150);
  }

  queueBuild() {
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.build(), 250);
  }

  syncOtherGame(select, option) {
    const wrap = this.querySelector('[data-other-game]');
    const input = this.querySelector('[data-other-input]');
    if (!select || !wrap || !input) return;
    if (!select.dataset.propName) select.dataset.propName = select.getAttribute('name');
    const isOther = !!(option && option.hasAttribute('data-other'));
    const wasHidden = wrap.hidden;
    wrap.hidden = !isOther;
    input.disabled = !isOther;
    input.required = isOther;
    if (isOther) {
      select.removeAttribute('name');
      input.setAttribute('name', select.dataset.propName);
      if (wasHidden) input.focus();
    } else {
      select.setAttribute('name', select.dataset.propName);
      input.removeAttribute('name');
    }
  }

  gameLabel() {
    const select = this.form.querySelector('select[data-summary="game"]');
    const option = select ? select.selectedOptions[0] : null;
    if (option && option.hasAttribute('data-other')) {
      const input = this.querySelector('[data-other-input]');
      return (input && input.value.trim()) || option.textContent.trim();
    }
    return select ? select.value : '';
  }

  update() {
    const game = this.form.querySelector('select[data-summary="game"]');
    const option = game ? game.selectedOptions[0] : null;
    if (option) this.applyProfile(option.dataset.profile || 'tactical');
    this.syncOtherGame(game, option);

    const nameField = this.form.querySelector('[data-summary="name"]');
    const nameOut = this.querySelector('[data-out="name"]');
    if (nameField && nameOut) nameOut.textContent = nameField.value.trim() || '—';

    for (const key of ['game', 'platform', 'style']) {
      const field = this.form.querySelector(`[data-summary="${key}"]:is(:checked, select)`);
      const out = this.querySelector(`[data-out="${key}"]`);
      if (field && out) out.textContent = key === 'game' ? this.gameLabel() : field.value;
    }

    const value = this.selectedMods().join(', ');
    const hidden = this.form.querySelector('[data-mods-value]');
    if (hidden) {
      hidden.value = value;
      hidden.disabled = value === '';
    }
    const modsOut = this.querySelector('[data-out="mods"]');
    if (modsOut) modsOut.textContent = value || '—';

    this.queueBuild();
  }
}

if (!customElements.get('script-configurator')) {
  customElements.define('script-configurator', ScriptConfigurator);
}
